// src/components/TrailerDetails.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaFileAlt, FaCalendarAlt } from 'react-icons/fa';
import axios from '../axiosConfig';
import '../styles/TrailerDetails.css';
import { useTranslation } from 'react-i18next';

/* ===== Helpers de URL/ficheiro ===== */
const API_BASE = (axios?.defaults?.baseURL || "").replace(/\/+$/, "");
const buildFileUrl = (p) => {
  if (!p) return "";
  const s = String(p);
  if (/^https?:\/\//i.test(s)) return s; // já é absoluto
  const clean = ("/" + s).replace(/\\/g, "/").replace(/\/{2,}/g, "/");
  return `${API_BASE}${clean}`;
};
const fileName = (p) => String(p || "").replace(/\\/g, "/").split("/").pop() || "";

function TrailerDetails() {
  const { t } = useTranslation('trailer_details');
  const { id } = useParams();
  const navigate = useNavigate();
  const [trailer, setTrailer] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!id) {
      setErrorMessage(t('errors.invalid_id'));
      return;
    }
    const fetchTrailerDetails = async () => {
      try {
        const { data } = await axios.get(`/trailers/${id}`);
        setTrailer(data);
      } catch {
        setErrorMessage(t('errors.load_fail'));
      }
    };
    fetchTrailerDetails();
  }, [id, t]);

  if (!trailer) {
    return <p>{errorMessage || t('loading')}</p>;
  }

  // Alguns backends podem enviar Month/month, etc.
  const monthNum = Number(trailer.month ?? trailer.Month ?? 0);
  const inspectionPath = trailer.inspection ?? trailer.Inspection ?? null;
  const vrcPath = trailer.vehicleRegistrationCertificate ?? trailer.VehicleRegistrationCertificate ?? null;

  const onEdit = () => {
    navigate(`/trailers/${id}/edit`, {
      state: {
        prefill: {
          trailerPlate: trailer.trailerPlate ?? '',
          brand: trailer.brand ?? '',
          type: trailer.type ?? '',
          month: monthNum || '',
          year: typeof trailer.year === 'string' ? Number(trailer.year) : trailer.year ?? '',
          status: trailer.status ?? '',
          height: trailer.height ?? '',
          width: trailer.width ?? '',
          length: trailer.length ?? '',
          weight: trailer.weight ?? '',
          numberOfAxles: trailer.numberOfAxles ?? '',
          numberOfStraps: trailer.numberOfStraps ?? '',
          numberOfRatchets: trailer.numberOfRatchets ?? '',
          numberOfEdgeProtectors: trailer.numberOfEdgeProtectors ?? '',
          numberOfAntiSlippingMattresses:
            trailer.numberOfAntiSlippingMattresses ?? trailer.numberOfAntiSlipMats ?? '',
          numberOfSidebars: trailer.numberOfSidebars ?? '',
          numberOfFireExtinguishers: trailer.numberOfFireExtinguishers ?? '',
          numberOfWoodenBeams: trailer.numberOfWoodenBeams ?? '',
          inspection: inspectionPath ?? null,
          inspectionExpiry: trailer.inspectionExpiry ?? trailer.InspectionExpiry ?? '',
          vehicleRegistrationCertificate: vrcPath ?? null,
          vehicleRegistrationCertificateExpiry:
            trailer.vehicleRegistrationCertificateExpiry ?? trailer.VehicleRegistrationCertificateExpiry ?? '',
        }
      }
    });
  };

  return (
    <div className="trailer-details-container">
      <div className="trailer-details-back-arrow" onClick={() => navigate('/trucks?view=trailers')}>
        ← {t('back')}
      </div>

      <h1 className="trailer-details-title">{t('title')}</h1>
      {errorMessage && <p className="trailer-details-error-message">{errorMessage}</p>}

      <form className="trailer-details-form">
        {/* Row 1 */}
        <div className="trailer-details-row">
          <div className="trailer-details-item">
            <label>{t('labels.trailer_plate')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.trailerPlate || t('na')} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.trailer_brand')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.brand || t('na')} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.trailer_type')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.type || t('na')} />
          </div>
        </div>

        {/* Row 2 */}
        <div className="trailer-details-row">
          <div className="trailer-details-item">
            <label>{t('labels.month')}</label>
            <input
              className="trailer-details-input"
              type="text"
              readOnly
              value={monthNum ? t(`months.${monthNum}`) : t('na')}
            />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.year')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.year || t('na')} />
          </div>
        </div>

        {/* Row 3 */}
        <div className="trailer-details-row">
          <div className="trailer-details-item">
            <label>{t('labels.width_m')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.width ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.length_m')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.length ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.height_m')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.height ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.weight_tons')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.weight ?? '0'} />
          </div>
        </div>

        {/* Row 4 */}
        <div className="trailer-details-row">
          <div className="trailer-details-item">
            <label>{t('labels.number_of_axles')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfAxles ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.number_of_straps')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfStraps ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.number_of_ratchets')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfRatchets ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.number_of_wooden_beams')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfWoodenBeams ?? '0'} />
          </div>
        </div>

        {/* Row 5 */}
        <div className="trailer-details-row">
          <div className="trailer-details-item">
            <label>{t('labels.number_of_edge_protectors')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfEdgeProtectors ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.number_of_anti_slip_mats')}</label>
            <input
              className="trailer-details-input"
              type="text"
              readOnly
              value={(trailer.numberOfAntiSlippingMattresses ?? trailer.numberOfAntiSlipMats) ?? '0'}
            />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.number_of_sidebars')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfSidebars ?? '0'} />
          </div>
          <div className="trailer-details-item">
            <label>{t('labels.number_of_fire_extinguishers')}</label>
            <input className="trailer-details-input" type="text" readOnly value={trailer.numberOfFireExtinguishers ?? '0'} />
          </div>
        </div>

        {/* Files + Expiries */}
        <div className="trailer-details-file-row trailer-details-file-row--4col">
          {/* Inspection file */}
          <div className="trailer-details-file-group">
            <label className="trailer-details-label">{t('labels.inspection_photo')}</label>
            <div className="trailer-details-upload-box">
              {inspectionPath ? (
                <a
                  href={buildFileUrl(inspectionPath)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="trailer-details-file-info"
                  title={fileName(inspectionPath)}
                >
                  <FaFileAlt className="trailer-details-file-icon" />
                  <span className="trailer-details-file-name">{fileName(inspectionPath)}</span>
                </a>
              ) : (
                <div className="trailer-details-upload-placeholder">
                  <p className="trailer-details-upload-text">{t('no_document')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Inspection expiry */}
          <div className="trailer-details-file-group">
            <label className="trailer-details-label">{t('labels.expiry_date')}</label>
            <div className="trailer-details-date-wrapper">
              <input
                type="text"
                readOnly
                className="trailer-details-date-input"
                value={
                  (trailer.inspectionExpiry || trailer.InspectionExpiry)
                    ? new Date(trailer.inspectionExpiry || trailer.InspectionExpiry).toLocaleDateString()
                    : t('na')
                }
              />
              <FaCalendarAlt className="trailer-details-calendar-icon" />
            </div>
          </div>

          {/* VRC file */}
          <div className="trailer-details-file-group">
            <label className="trailer-details-label">{t('labels.vehicle_registration_certificate')}</label>
            <div className="trailer-details-upload-box">
              {vrcPath ? (
                <a
                  href={buildFileUrl(vrcPath)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="trailer-details-file-info"
                  title={fileName(vrcPath)}
                >
                  <FaFileAlt className="trailer-details-file-icon" />
                  <span className="trailer-details-file-name">{fileName(vrcPath)}</span>
                </a>
              ) : (
                <div className="trailer-details-upload-placeholder">
                  <p className="trailer-details-upload-text">{t('no_document')}</p>
                </div>
              )}
            </div>
          </div>

          {/* VRC expiry */}
          <div className="trailer-details-file-group">
            <label className="trailer-details-label">{t('labels.expiry_date')}</label>
            <div className="trailer-details-date-wrapper">
              <input
                type="text"
                readOnly
                className="trailer-details-date-input"
                value={
                  (trailer.vehicleRegistrationCertificateExpiry || trailer.VehicleRegistrationCertificateExpiry)
                    ? new Date(trailer.vehicleRegistrationCertificateExpiry || trailer.VehicleRegistrationCertificateExpiry).toLocaleDateString()
                    : t('na')
                }
              />
              <FaCalendarAlt className="trailer-details-calendar-icon" />
            </div>
          </div>
        </div>

        {/* Edit dentro do form */}
        <div className="trailer-details-form-buttons">
          <button
            type="button"
            className="trailer-details-edit-btn"
            onClick={onEdit}
          >
            {t('buttons.edit')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TrailerDetails;
